import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
    ServiceRequestFormData,
    OrganizationData,
    LocationType,
    Category
} from '../../types';
import { useMachineData } from '../../hooks/useMachineData';
import { useMakesByCategory } from '../../hooks/useMakesByCategory';
import './StepStyles.scss';

interface Step4MachineProps {
  formData: ServiceRequestFormData;
  updateFormData: (data: Partial<ServiceRequestFormData>) => void;
  registerValidation: (validator: () => boolean) => void;
  organizationData: OrganizationData;
}

type CategoryOption =
  | { kind: 'display'; id: number; name: string; categoryIds: number[] }
  | { kind: 'category'; id: number; name: string; categoryIds: [number] };

function categoryMatchesLocationType(category: Category | undefined, locationType: LocationType | null): boolean {
  if (!category) return false;
  if (!locationType || locationType === 'combo') return true;
  return category.general_type === locationType;
}

function optionKey(opt: CategoryOption): string {
  return `${opt.kind === 'display' ? 'd' : 'c'}:${opt.id}`;
}

function Step4Machine({
  formData,
  updateFormData,
  registerValidation,
  organizationData,
}: Step4MachineProps) {
  const [validationError, setValidationError] = useState<string | null>(null);

  // Get the selected location and its type
  const selectedLocation = useMemo(() => {
    return organizationData.locations.find(loc => loc.id === formData.selectedLocationId) || null;
  }, [organizationData.locations, formData.selectedLocationId]);

  const selectedLocationType: LocationType | null = selectedLocation?.location_type || null;

  const orgEnabledCategoryIds = useMemo(
    () => new Set(organizationData.organizationCategories.map(oc => oc.category_id)),
    [organizationData.organizationCategories],
  );

  const allCategoriesById = useMemo(
    () => new Map(organizationData.allCategories.map(c => [c.id, c] as const)),
    [organizationData.allCategories],
  );

  // Mapping from display_category -> [internal category ids], filtered to org-enabled categories
  const mappingsByDisplayCat = useMemo(() => {
    const map = new Map<number, number[]>();
    for (const m of organizationData.displayCategoryMappings) {
      if (!orgEnabledCategoryIds.has(m.category_id)) continue;
      const arr = map.get(m.display_category_id);
      if (arr) arr.push(m.category_id);
      else map.set(m.display_category_id, [m.category_id]);
    }
    return map;
  }, [organizationData.displayCategoryMappings, orgEnabledCategoryIds]);

  // Internal category IDs that are covered by *any* display_category mapping.
  // These should NOT appear as raw categories in the dropdown — they're surfaced via their display_category.
  const coveredCategoryIds = useMemo(() => {
    const covered = new Set<number>();
    for (const ids of mappingsByDisplayCat.values()) {
      for (const id of ids) covered.add(id);
    }
    return covered;
  }, [mappingsByDisplayCat]);

  // Unified, sorted list of options shown in the first dropdown:
  //   - Each display_category with mappings that survive the location-type filter
  //   - Each org-enabled internal category that is (a) NOT covered by a display_category AND (b) matches the location type
  const categoryOptions: CategoryOption[] = useMemo(() => {
    const displayOpts: CategoryOption[] = [];
    for (const dc of organizationData.displayCategories) {
      const mapped = mappingsByDisplayCat.get(dc.id);
      if (!mapped || mapped.length === 0) continue;
      const filtered = mapped.filter(cid =>
        categoryMatchesLocationType(allCategoriesById.get(cid), selectedLocationType),
      );
      if (filtered.length === 0) continue;
      displayOpts.push({ kind: 'display', id: dc.id, name: dc.name, categoryIds: filtered });
    }

    const rawOpts: CategoryOption[] = [];
    for (const cat of organizationData.allCategories) {
      if (!orgEnabledCategoryIds.has(cat.id)) continue;
      if (coveredCategoryIds.has(cat.id)) continue;
      if (!categoryMatchesLocationType(cat, selectedLocationType)) continue;
      rawOpts.push({ kind: 'category', id: cat.id, name: cat.name, categoryIds: [cat.id] });
    }

    return [...displayOpts, ...rawOpts].sort((a, b) => a.name.localeCompare(b.name));
  }, [
    organizationData.displayCategories,
    organizationData.allCategories,
    mappingsByDisplayCat,
    coveredCategoryIds,
    orgEnabledCategoryIds,
    allCategoriesById,
    selectedLocationType,
  ]);

  // Resolve the currently selected option from form state
  const selectedOption: CategoryOption | null = useMemo(() => {
    if (formData.selectedDisplayCategoryId) {
      return categoryOptions.find(o => o.kind === 'display' && o.id === formData.selectedDisplayCategoryId) ?? null;
    }
    if (formData.selectedCategoryId) {
      return categoryOptions.find(o => o.kind === 'category' && o.id === formData.selectedCategoryId) ?? null;
    }
    return null;
  }, [categoryOptions, formData.selectedDisplayCategoryId, formData.selectedCategoryId]);

  // Internal category IDs that drive the Make/Machine queries
  const mappedCategoryIds: number[] | null = useMemo(() => {
    return selectedOption ? selectedOption.categoryIds : null;
  }, [selectedOption]);

  // Use the machine data hook to fetch machines on-demand
  const { machines: availableMachines, loading: machinesLoading, error: machinesError } =
    useMachineData(mappedCategoryIds, formData.selectedMakeId ?? null);

  // Fetch valid make IDs for the active internal category set
  const { makeIds: makeIdsInCategory, loading: makesLoading } =
    useMakesByCategory(mappedCategoryIds);

  // Filter Makes available to this organization, further filtered by the selected category set
  const availableMakes = useMemo(() => {
    const orgMakeIds = new Set(organizationData.organizationMakes.map(om => om.make_id));
    let filtered = organizationData.allMakes.filter(make => orgMakeIds.has(make.id));

    if (mappedCategoryIds && makeIdsInCategory.size > 0) {
      filtered = filtered.filter(make => makeIdsInCategory.has(make.id));
    }

    return filtered;
  }, [organizationData.allMakes, organizationData.organizationMakes, mappedCategoryIds, makeIdsInCategory]);

  // --- Event Handlers ---

  const handleCategorySelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const raw = e.target.value;
    if (!raw) {
      updateFormData({
        selectedDisplayCategoryId: null,
        selectedCategoryId: null,
        selectedMakeId: null,
        selectedMachineId: null,
        selectedMachineName: null,
      });
      setValidationError(null);
      return;
    }
    const [kind, idStr] = raw.split(':');
    const id = parseInt(idStr, 10);
    if (!Number.isFinite(id)) return;

    if (kind === 'd') {
      // Display category picked — internal category resolves at machine-pick time
      updateFormData({
        selectedDisplayCategoryId: id,
        selectedCategoryId: null,
        selectedMakeId: null,
        selectedMachineId: null,
        selectedMachineName: null,
      });
    } else {
      // Raw internal category picked directly
      updateFormData({
        selectedDisplayCategoryId: null,
        selectedCategoryId: id,
        selectedMakeId: null,
        selectedMachineId: null,
        selectedMachineName: null,
      });
    }
    setValidationError(null);
  };

  const handleMakeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const makeId = e.target.value ? parseInt(e.target.value, 10) : null;
    const update: Partial<ServiceRequestFormData> = {
      selectedMakeId: makeId,
      selectedMachineId: null,
      selectedMachineName: null,
    };
    // If a display_category is active, the internal category is only known after machine-pick
    if (formData.selectedDisplayCategoryId) {
      update.selectedCategoryId = null;
    }
    updateFormData(update);
    setValidationError(null);
  };

  const handleMachineChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const machineId = e.target.value ? parseInt(e.target.value, 10) : null;
    const selectedMachine = machineId ? availableMachines.find(m => m.id === machineId) : null;

    // Always resolve the internal category from the machine. For raw-category selections this is a no-op
    // (the value is already that category); for display_category selections this is the critical step
    // that lets packageFilters / hauling / discounts / the edge function keep using the internal category.
    updateFormData({
      selectedMachineId: machineId,
      selectedMachineName: selectedMachine?.name || null,
      selectedCategoryId: selectedMachine?.category_id ?? formData.selectedCategoryId ?? null,
    });
    setValidationError(null);
  };

  // --- Validation ---
  const validateStep = useCallback(() => {
    if (!formData.selectedDisplayCategoryId && !formData.selectedCategoryId) {
      setValidationError("Please select a category.");
      return false;
    }
    if (!formData.selectedMakeId) {
      setValidationError("Please select a make.");
      return false;
    }
    if (!formData.selectedMachineId) {
      setValidationError("Please select a machine.");
      return false;
    }
    if (!formData.selectedCategoryId) {
      // Display-category picked but machine didn't resolve an internal category — shouldn't happen
      setValidationError("Could not resolve machine category. Please re-select your machine.");
      return false;
    }

    setValidationError(null);
    return true;
  }, [
    formData.selectedDisplayCategoryId,
    formData.selectedCategoryId,
    formData.selectedMakeId,
    formData.selectedMachineId,
  ]);

  useEffect(() => {
    registerValidation(validateStep);
  }, [registerValidation, validateStep]);

  // --- Reset selection if the previously chosen option is no longer in the list (e.g. location changed) ---
  useEffect(() => {
    const hasSelection = formData.selectedDisplayCategoryId != null || formData.selectedCategoryId != null;
    if (!hasSelection) return;
    if (categoryOptions.length === 0) return;
    if (selectedOption) return; // still valid

    updateFormData({
      selectedDisplayCategoryId: null,
      selectedCategoryId: null,
      selectedMakeId: null,
      selectedMachineId: null,
      selectedMachineName: null,
    });
  }, [categoryOptions, selectedOption, formData.selectedDisplayCategoryId, formData.selectedCategoryId, updateFormData]);

  // --- Reset make selection if it becomes invalid after category change ---
  useEffect(() => {
    if (formData.selectedMakeId && availableMakes.length > 0) {
      const isMakeValid = availableMakes.some(make => make.id === formData.selectedMakeId);
      if (!isMakeValid) {
        updateFormData({
          selectedMakeId: null,
          selectedMachineId: null,
          selectedMachineName: null,
        });
      }
    }
  }, [availableMakes, formData.selectedMakeId, updateFormData]);

  // Show a message if no location is selected yet
  if (!formData.selectedLocationId) {
    return (
      <div className="form-step step-machine">
        <h2>Machine Details</h2>
        <div className="notification-message">
          Please select a service location in the previous step before configuring machine details.
        </div>
      </div>
    );
  }

  // Show a message if location doesn't support any categories
  if (categoryOptions.length === 0 && selectedLocation) {
    return (
      <div className="form-step step-machine">
        <h2>Machine Details</h2>
        <div className="notification-message">
          {`The selected ${selectedLocation.name} location does not support any equipment categories. Please select a different location or contact customer service.`}
        </div>
      </div>
    );
  }

  const selectValue = selectedOption ? optionKey(selectedOption) : '';

  return (
    <div className="form-step step-machine">
      <h2>Machine Details</h2>
      <p className="step-intro">
        Please select the category, make, and model of the machine requiring service.
      </p>

      {selectedLocation && (
        <p className="location-type-notice">
          {selectedLocationType === 'ag' && "This location specializes in AG equipment."}
          {selectedLocationType === 'lawn_garden' && "This location specializes in Lawn & Garden equipment."}
          {selectedLocationType === 'combo' && "This location services both AG and Lawn & Garden equipment."}
        </p>
      )}

      {validationError && (
        <div className="error-message summary-error">{validationError}</div>
      )}

      {/* Category Dropdown */}
      <div className="form-group">
        <label htmlFor="selectedCategoryOption" className="required">Category</label>
        <select
          id="selectedCategoryOption"
          name="selectedCategoryOption"
          value={selectValue}
          onChange={handleCategorySelectChange}
          required
        >
          <option value="" disabled>Select Category...</option>
          {categoryOptions.map((opt) => (
            <option key={optionKey(opt)} value={optionKey(opt)}>{opt.name}</option>
          ))}
        </select>
      </div>

      {/* Make Dropdown */}
      <div className="form-group">
        <label htmlFor="selectedMakeId" className="required">Make</label>
        <select
          id="selectedMakeId"
          name="selectedMakeId"
          value={formData.selectedMakeId ?? ''}
          onChange={handleMakeChange}
          disabled={!mappedCategoryIds || makesLoading || availableMakes.length === 0}
          required
        >
          <option value="" disabled>
             { !mappedCategoryIds ? "Select Category first..." :
               makesLoading ? "Loading makes..." :
               availableMakes.length === 0 ? "No makes available..." :
               "Select Make..." }
          </option>
          {availableMakes.map((make) => (
            <option key={make.id} value={make.id}>
              {make.name}
            </option>
          ))}
        </select>
      </div>

      {/* Machine Dropdown */}
      <div className="form-group">
        <label htmlFor="selectedMachineId" className="required">Machine / Model</label>
        <select
          id="selectedMachineId"
          name="selectedMachineId"
          value={formData.selectedMachineId ?? ''}
          onChange={handleMachineChange}
          disabled={!formData.selectedMakeId || machinesLoading}
          required
        >
          <option value="" disabled>
            { !formData.selectedMakeId ? "Select Make first..." :
              machinesLoading ? "Loading machines..." :
              machinesError ? "Error loading machines" :
              availableMachines.length === 0 ? "No machines match criteria" :
              "Select Machine..." }
          </option>
          {availableMachines.map((machine) => (
            <option key={machine.id} value={machine.id}>
              {machine.name}
            </option>
          ))}
        </select>
        {machinesError && <div className="error-message">Error: {machinesError}</div>}
      </div>
    </div>
  );
}

export default Step4Machine;
